import { Typography, useEventCallback } from '@mui/material';
import FilterNotifications from 'civil-status/components/research-notifications/filter';
import { ResearchNotificationsCrumbs } from 'civil-status/constants/breadcrumb';
import { CriteriRicercaNotifiche, NotificaRes } from 'civil-status/model/notifications';
import { transformNotificationsResearch } from 'civil-status/utils/transform-notifications-research';
import {
  NotificationsList,
  ResearchNotifications,
} from 'civil-status/view-models/notifications-list';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { transformNotificationsList } from 'civil-status/utils/transformer-notifications-list';
import TableNotifications from 'civil-status/components/research-notifications/table';
import { useFilterTable } from 'shell/hooks/use-filter-table';

const ResearchNotificationsPage = (): ReactElement => {
  const params = useParams();

  const nameCat = params.valueCat || undefined;
  const idCat = params.idCat || undefined;

  const filterTable = useFilterTable<ResearchNotifications>();

  // filtri persona
  const [openFilterPerson, setOpenFilterPerson] = useState<boolean>(true);
  // filtri atto
  const [openNotifications, setOpenNotifications] = useState<boolean>(true);
  // filtri selezionati
  const [filterSelected, setFilterSelected] = useState<CriteriRicercaNotifiche | undefined>({});
  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const [notificationsList, getNotificationsList] = useHttpFetch<
    HttpResponse<NotificaRes>,
    Array<NotificationsList>
  >(`${__ACT_API_URL__}/notifiche/ricerca`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformNotificationsList,
  });

  const getNotificationsListData = useEventCallback(
    (dataFilter?: CriteriRicercaNotifiche, page?: number, rowsPerPage?: number) => {
      (
        getNotificationsList({
          data: {
            ...dataFilter,
            datiPaginazione: {
              numPagina: page || 0,
              numRecordPagina: rowsPerPage || 10,
            },
          },
        }) as unknown as ResponsePromise<void>
      ).then(() => {
        // salvo i filtri dell'ultima ricerca
        setFilterSelected(dataFilter);
      });
    }
  );

  const handleSubmitResearchNotifications = useEventCallback(
    (values: ResearchNotifications, page?: number, rowsPerPage?: number) => {
      // mantenere dati
      filterTable.setValues({
        typeEvent: values.typeEvent,
        disabledTypeEvent: Boolean(idCat),
        dateEnd: values.dateEnd,
        dateStart: values.dateStart,
        idAnsc: values.idAnsc,
        typeContent: values.typeContent,
        state: values.state,
        typeNotifications: values.typeNotifications,
        stateNotification: values.stateNotification,
        isResearchNotifications: true,
      });

      const dataFilter: CriteriRicercaNotifiche = transformNotificationsResearch(values);

      setTableProps({
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      });

      getNotificationsListData(dataFilter);
    }
  );

  const handleSubmitResearchPerson = useEventCallback(
    (values: ResearchNotifications, page?: number, rowsPerPage?: number) => {
      // mantenere dati
      filterTable.setValues({
        birthDate: values.birthDate,
        fiscalCode: values.fiscalCode,
        name: values.name,
        surname: values.surname,
        idAnpr: values.idAnpr,
        idAnsc: values.idAnsc,
        isForeign: values.isForeign,
        municipality: values.municipality,
        state: values.state,
        gender: values.gender,
        isResearchPerson: true,
      });

      const dataFilter: CriteriRicercaNotifiche = transformNotificationsResearch(values);

      setTableProps({
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      });

      getNotificationsListData(dataFilter);
    }
  );

  useEffect(() => {
    if (((notificationsList.data as Array<NotificationsList>) || []).length) {
      // se abbiamo risultato chiudere accordion dei filtri
      setOpenFilterPerson(false);
      setOpenNotifications(false);
    }
  }, [notificationsList.data]);

  useEffect(() => {
    if (filterTable.values?.isResearchPerson) {
      handleSubmitResearchPerson(
        filterTable.values,
        filterTable.values.tableProps?.page,
        filterTable.values.tableProps?.rowsPerPage
      );
    }

    if (filterTable.values?.isResearchNotifications) {
      handleSubmitResearchNotifications(
        filterTable.values,
        filterTable.values.tableProps?.page,
        filterTable.values.tableProps?.rowsPerPage
      );
    }
  }, []);

  const setTablePropsFilterTable = useEventCallback((page?: number, rowsPerPage?: number) => {
    filterTable.setValues({
      ...filterTable.values,
      tableProps: {
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      },
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={ResearchNotificationsCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          {`Ricerca diretta in ANSC ${nameCat || ''}`}
        </Typography>
      </div>
      <FilterNotifications
        openPerson={openFilterPerson}
        setOpenPerson={setOpenFilterPerson}
        openNotifications={openNotifications}
        setOpenNotifications={setOpenNotifications}
        handleSubmitResearchNotifications={handleSubmitResearchNotifications}
        handleSubmitResearchPerson={handleSubmitResearchPerson}
        idCat={idCat}
        initialValues={filterTable.values}
      />
      {notificationsList.data ? (
        ((notificationsList.data as Array<NotificationsList>) || [])?.length > 0 ? (
          <>
            <TableNotifications
              data={notificationsList.data as Array<NotificationsList>}
              filterSelected={filterSelected}
              getNotificationsListData={getNotificationsListData}
              idCat={idCat}
              nameCat={nameCat}
              tableProps={tableProps}
              setTableProps={setTableProps}
              setTablePropsFilterTable={setTablePropsFilterTable}
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

ResearchNotificationsPage.displayName = 'Research Notifications Page component';

export default ResearchNotificationsPage;
